/**
 * ArcheSearchCombobox — "arche name or id" input with debounced typeahead
 * over PUBLISHED plugin arches, resolved server-side (an exact arc_ id
 * resolves to that one arche). Same shape as UserSearchCombobox: a plain
 * input plus a positioned listbox, keyboard navigation, and the list
 * preventDefaults mousedown so the input keeps focus while picking.
 *
 * In a Company's own picker (`companyScope`) the search also runs with
 * nothing typed, once the field is focused: the server answers with the
 * Company's own arches — the ones it owns or holds a grant on — ahead of the
 * store, shown under their own heading. A restricted arche is never listed in
 * the store, so this is how a Company finds its own landing page or approves
 * its own app (ADR-026).
 */

import { Fragment, useId, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { ArcheSearchDto, ArcheSearchResultDto } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';

interface ArcheSearchComboboxProps {
  /** Cache scope — the holder or Company the search is authorized on. */
  scopeKey: string;
  search: (q: string) => Promise<ArcheSearchDto>;
  value: string;
  onValueChange: (value: string) => void;
  onSelect: (arche: ArcheSearchResultDto) => void;
  id?: string;
  placeholder?: string;
  disabled?: boolean;
  /**
   * Set for a Company's own picker: searches on focus with nothing typed, and
   * names the Company in the headings and labels when its name is known.
   */
  companyScope?: { name?: string };
}

export function publisherLabel(r: ArcheSearchResultDto, companyName = 'this Company'): string {
  if (r.companyRelation === 'owned') return `Owned by ${companyName}`;
  if (r.companyRelation === 'granted') return `Shared with ${companyName}`;
  if (r.isPlatformOwned) return 'Published by Fias';
  return r.contributorId ? `Published by user …${r.contributorId.slice(-6)}` : 'Unknown publisher';
}

const isOwn = (r: ArcheSearchResultDto) =>
  r.companyRelation === 'owned' || r.companyRelation === 'granted';

export function ArcheSearchCombobox({
  scopeKey,
  search,
  value,
  onValueChange,
  onSelect,
  id,
  placeholder = 'Search published arches, or paste an arc_… id',
  disabled,
  companyScope,
}: ArcheSearchComboboxProps) {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const listId = useId();
  const companyName = companyScope?.name ?? 'this Company';
  const ownHeading = companyScope?.name ? `${companyScope.name} arches` : 'This Company’s arches';
  // The store search needs a term; a Company's own arches are offered on focus.
  const minQueryLength = companyScope ? 0 : 2;

  const query = value.trim();
  const debounced = useDebouncedValue(query, 250);
  const searchable = query.length >= minQueryLength;
  const { data, isFetching, isError } = useQuery({
    queryKey: queryKeys.companies.archeSearch(scopeKey, debounced),
    queryFn: () => search(debounced),
    // Not before the field is focused: a mounted-but-untouched picker asks nothing.
    enabled: open && debounced.length >= minQueryLength,
    staleTime: 30_000,
  });
  const results = data?.results ?? [];
  const builtIn = data?.builtIn ?? [];
  const isDebouncing = searchable && query !== debounced;
  // The server puts the Company's own arches first; count them to place the headings.
  let ownCount = 0;
  while (ownCount < results.length && isOwn(results[ownCount])) ownCount++;

  const showList = open && searchable;
  const safeActiveIndex = activeIndex < results.length ? activeIndex : -1;

  const close = () => {
    setOpen(false);
    setActiveIndex(-1);
  };
  const choose = (arche: ArcheSearchResultDto) => {
    onValueChange(arche.name);
    onSelect(arche);
    close();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showList || results.length === 0) {
      if (e.key === 'Escape') close();
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % results.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => (i <= 0 ? results.length - 1 : i - 1));
    } else if (e.key === 'Enter') {
      // Enter picks the highlighted match, or the only match for something
      // typed (a pasted id). An empty field offering one arche picks nothing:
      // Enter there is the form's.
      const pick =
        safeActiveIndex >= 0
          ? results[safeActiveIndex]
          : results.length === 1 && query.length > 0
            ? results[0]
            : null;
      if (pick) {
        e.preventDefault();
        choose(pick);
      }
    } else if (e.key === 'Escape') {
      close();
    }
  };

  const heading = (text: string) => (
    <li role="presentation" className="px-3 pb-0.5 pt-2 text-xs font-medium text-muted-foreground">
      {text}
    </li>
  );

  return (
    <div className="relative">
      <input
        type="text"
        id={id}
        value={value}
        disabled={disabled}
        placeholder={placeholder}
        maxLength={100}
        autoComplete="off"
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={showList}
        aria-controls={listId}
        aria-activedescendant={
          showList && safeActiveIndex >= 0 ? `${listId}-option-${safeActiveIndex}` : undefined
        }
        onChange={(e) => {
          onValueChange(e.target.value);
          setOpen(true);
          setActiveIndex(-1);
        }}
        onFocus={() => setOpen(true)}
        onBlur={close}
        onKeyDown={handleKeyDown}
        className="w-full rounded border border-border bg-background px-3 py-2 text-sm"
      />
      {showList && results.length > 0 && (
        <ul
          id={listId}
          role="listbox"
          aria-label="Matching arches"
          className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded border border-border bg-card shadow-lg"
          onMouseDown={(e) => e.preventDefault()}
        >
          {results.map((r, index) => (
            <Fragment key={r.archeId}>
              {ownCount > 0 && index === 0 && heading(ownHeading)}
              {ownCount > 0 && index === ownCount && heading('Other published arches')}
              <li
                id={`${listId}-option-${index}`}
                role="option"
                aria-selected={index === safeActiveIndex}
              >
                <button
                  type="button"
                  tabIndex={-1}
                  onClick={() => choose(r)}
                  onMouseEnter={() => setActiveIndex(index)}
                  className={`block w-full px-3 py-2 text-left text-sm ${
                    index === safeActiveIndex ? 'bg-muted/40' : 'hover:bg-muted/40'
                  }`}
                >
                  <span className="font-medium">{r.name}</span>
                  <span className="ml-2 text-xs text-muted-foreground">
                    {publisherLabel(r, companyName)} · …{r.archeId.slice(-6)}
                  </span>
                </button>
              </li>
            </Fragment>
          ))}
        </ul>
      )}
      {showList && results.length === 0 && (
        <div className="absolute z-10 mt-1 w-full rounded border border-border bg-card px-3 py-2 text-sm text-muted-foreground shadow-lg">
          {isFetching || isDebouncing
            ? 'Searching…'
            : isError
              ? 'Search failed — try again.'
              : builtIn.length > 0
                ? `${builtIn.map((b) => b.name).join(', ')} ${builtIn.length === 1 ? 'is a built-in Fias feature' : 'are built-in Fias features'}: ${builtIn.length === 1 ? 'it follows' : 'they follow'} each member’s workspace role and ${builtIn.length === 1 ? 'needs' : 'need'} no approval. Approvals apply to installable arches.`
                : query === ''
                  ? `${companyScope?.name ?? 'This Company'} has no published arches of its own yet. Type a name to search published arches, or paste an arc_… id.`
                  : 'No installable arche matches. Check the store, or paste its arc_… id.'}
        </div>
      )}
    </div>
  );
}
